import { enPaymentMethod, enNormalizedPaymentType } from 'enums/enPaymentMethod';
import { enPaymentType } from 'enums/enPaymentType';
import { enCurrency, enLang, IProductData, IShipping, IShippingData, ICouponData } from 'interfaces/IProductData';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { paymentService } from 'services/PaymentService';
import { CACHED_MOUNT, CART_SSID, IGNORE_CACHE, TRACKER_DATA, IS_ECOMMERCE, GOOFY_ID } from 'settings';
import { round2 } from 'shared/functions';
import { Cookies } from 'react-cookie';

import { IProductConfig, IProductTemplateConfig } from '../interfaces/IProductData';
import { CACHE_ID } from '../settings';
import { cacheService } from './CacheService';
import { currencyService } from './CurrencyService';
import { warmapCache } from './InstallmentsService';
import { parameterService } from './ParameterService';
import { apiService } from 'shared/services';
import { ICartModel } from 'interfaces/ICartModel';
import { socketService } from './SocketService';
import { pixelService } from 'services/PixelService';
import { translateService } from './TranslateService';
import { enProductType } from 'enums/enProductType';
import { enParameters } from 'enums/enParameters';
import { ticketService } from 'services/TicketService';
import { of } from 'rxjs/observable/of';
import { formatScripts } from 'functions';
import { scriptsService } from './ScriptsService';
import { showAlert } from 'shared/services/dialog';

export interface ICartConfigReplacer {
  templateId?: number;
  template?: IProductTemplateConfig;
}

const cookie = new Cookies();

export class CartService {
  private cart$ = new ReplaySubject<IProductData>(1);

  private configReplacer$ = new BehaviorSubject<ICartConfigReplacer>({});

  private loaded = false;
  private mounted = false;

  public getCart(): Observable<IProductData> {
    return this.cart$
      .asObservable()
      .map(state => {
        (state.isCached as any) = !this.mounted;
        return state;
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  public getConfig(): Observable<IProductConfig> {
    return this.getCart()
      .map(data => (data as any).config)
      .combineLatest(this.configReplacer$)
      .map(([config, replacer]) => {
        (config.template as any) = replacer.template || config.template;
        (config.template as any).id = Number(config.template.id);
        return config;
      });
  }

  public setShippingData(quantity: number, postalCode: string = null): Observable<IShippingData> {

    return paymentService.getCart()
      .take(1)
      .switchMap(payment => {
        const body = {
          transactionId: payment.ssid,
          postalCode,
          products: [
            {
              contentId: payment.contentId,
              quantity
            }
          ]
        };

        if (body.postalCode == 'undefined') {
          body.postalCode = null;
        }

        return apiService.post<IShippingData>('/shipping/estimate', body).loader();
      })
      .combineLatest(cartService.getCart().take(1))
      .map(([data, cart]) => {
        data.products.forEach(p => {
          warmapCache({
            installments: p.installments,
            price: p.price
          });
        });

        data.shippingItems.forEach(i => {
          warmapCache({
            installments: i.installments,
            price: i.totalValue
          });
        });

        if (data.shippingItems.length === 0) {
          const newCart: IProductData = JSON.parse(JSON.stringify(cart));
          (newCart.installments as any) = data.products[0].installments;
          (newCart.price as any) = data.products[0].price;
          (newCart.quantity as any) = data.products[0].quantity;

          if (newCart.config.productType == enProductType.TICKET) {
            newCart.tickets.reservationData = data.reservation;

            const hasError = cart.tickets.reservationData.products.some(p => p.success == false);

            if (hasError) {
              // tslint:disable-next-line:max-line-length
              showAlert('Infelizmente a quantidade de tickets selecionada é maior que o nosso estoque atual. Tente novamente mais tarde.', 'Ops!').subscribe();
            }

          }
          this.cart$.next(newCart);

          if (newCart.tickets) {
            ticketService.setTimeLeft(new Date().getTime() + 1000 * newCart.tickets.reservationData.expiresIn);
          }

          paymentService.setData({
            quantity: data.products[0].quantity
          });

          paymentService.setPaymentData(0, {
            value: data.products[0].price,
            installments: data.products[0].installments,
            numberInstallments: data.products[0].installments.length
          });

          return data;
        }

        this.setShipping(data.hash, data.shippingItems[0]);

        return data;
      });
  }

  public setShipping(hash: string, selectedShipping: IShipping): void {

    this.getCart()
      .take(1)
      .map((cart) => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));

        (newCart.price as any) = selectedShipping.totalValue;
        (newCart.installments as any) = selectedShipping.installments;

        this.cart$.next(newCart);
        paymentService.setData({
          shipping: {
            code: selectedShipping.code,
            hash
          }
        });
        paymentService.setPaymentData(0, {
          value: selectedShipping.totalValue,
          installments: selectedShipping.installments,
          numberInstallments: selectedShipping.installments.length
        });

        return true;
      })
      .subscribe();

  }

  public setCoupon(coupon: string, couponType: number = 1): Observable<boolean> {
    return this.getCart()
      .filter(cart => !!cart.transactionKey)
      .take(1)
      .switchMap(cart => {

        const url = couponType === 1
          ? `/coupon/${cart.transactionKey}/product/${cart.contentId}`
          : `/coupon/${cart.transactionKey}/product/${cart.contentId}/modal`;

        return apiService.post<ICouponData>(url, {
          coupon
        });
      })
      .switchMap((coupon) => this.updateCouponData(coupon));
  }

  public enableAskAddress(): Observable<IProductData> {
    return this.getCart()
      .take(1)
      .filter(cart => cart.config.askAddressAfterPayment || cart.config.askAddressBeforePayment)
      .do((cart) => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));

        (newCart.config.askAddressAfterPayment as any) = false;
        (newCart.config.askAddressBeforePayment as any) = true;

        this.cart$.next(newCart);
      });
  }

  public removeCoupon(): Observable<boolean> {
    return this.getCart()
      .take(1)
      .switchMap(cart => {
        return apiService.del<ICouponData>(`/coupon/${cart.transactionKey}/product/${cart.contentId}`);
      })
      .switchMap((coupon) => this.updateCouponData(coupon));
  }

  public updateCouponData(coupon: ICouponData): Observable<boolean> {
    return Observable.of(coupon)
      .combineLatest(this.getCart().take(1), paymentService.getCart().take(1))
      .map(([coupon, cart, payment]) => {
        const newCart = this.createNewCart(cart, coupon);

        // REMOVER APÓS CORRIGIDO NA API
        if (newCart.paymentType === enPaymentType.SUBSCRIPTION) {
          (newCart.subscription.tax as any) = coupon.product.price;
        }

        warmapCache(newCart);

        this.cart$.next(newCart);

        const newValue = round2(newCart.price / payment.payment.length);

        let lastPaymentValue = newValue + (newCart.price - newValue * payment.payment.length);

        lastPaymentValue = round2(lastPaymentValue);

        payment.payment.map((p, key) => {
          const selectedInstallment = p.numberInstallments;

          const maxInstallments = newCart.maxInstallments < newCart.installments.length ? newCart.maxInstallments : newCart.installments.length;

          const numberInstallments = maxInstallments >= selectedInstallment ? selectedInstallment : maxInstallments;

          paymentService.setPaymentData(key, {
            value: key != payment.payment.length - 1 ? newValue : lastPaymentValue,
            installments: newCart.installments,
            numberInstallments
          });
        });

        return true;
      })
      .logError();
  }

  public disableOneClickBuy(): Observable<void> {

    cookie.remove('he');
    cookie.remove('email');

    return this.getCart()
      .take(1)
      .map(cart => {
        let newCart: IProductData = JSON.parse(JSON.stringify(cart));
        (newCart.config.isOneClick as any) = false;
        this.cart$.next(newCart);
      });
  }

  public setConfig(name: string, value: any) {
    const config = JSON.parse(JSON.stringify(this.configReplacer$.value));

    config[name] = value;

    this.configReplacer$.next(config);
  }

  public getCurrentState(): Observable<IProductData> {
    return this.getCart().first();
  }

  public loadContent(contentId: string, lang: enLang, currency: enCurrency): Observable<boolean> {

    let transactionKey = CART_SSID;

    const cachedMount = CACHED_MOUNT;

    let isEcommerce = false;

    let loadObservable = parameterService.getAll()
      .take(1)
      .switchMap((parameters) => {
        if (cachedMount) {
          return of(parameters).delay(3000);
        }
        return of(parameters);
      })
      .switchMap(parameters => {

        if (IS_ECOMMERCE || !!`${contentId}`.match(/^c_/)) {
          contentId = contentId.replace(/^c_/, '');
          parameters = { ...parameters, ec: '1' };
          isEcommerce = true;
        }

        const cookie = new Cookies();

        const lastPurchase = cookie.get('last_purchase');
        if (parameters.u && lastPurchase) {
          delete parameters.u;
          parameters.upsell = true;

          const [paymentMethod, newTransactionKey] = lastPurchase.split(':');

          if (paymentMethod == enNormalizedPaymentType.BANKSLIP) {
            transactionKey = newTransactionKey;
          }
        }

        return apiService.put<IProductData>(`/cart/${transactionKey}`, {
          contentId,
          currency,
          parameters,
          tracker: TRACKER_DATA,
          goofyId: GOOFY_ID
        });
      })
      .catch(err => {
        if (err.code === 'TRA_A') {
          cookie.remove(`goofy-${contentId}`);
          location.reload();
          return Observable.empty();
        }

        return Observable.throw(err);
      })
      .map((data: IProductData) => {
        (data.config.scripts as any) = formatScripts(data.config.scripts);
        (data.isCached as any) = false;

        scriptsService.setScripts(data.config.scripts);

        return data;
      });

    if (!this.loaded) {
      this.loaded = true;
      loadObservable = loadObservable.startWith(cachedMount);
    }

    const observable = loadObservable
      .filter(data => !!data)
      .combineLatest(paymentService.getCart().take(1))
      .map(([data, paymentData]) => {

        if (['b', 'p'].includes(data.paymentMethod)) {
          parameterService.setParameter(enParameters.pm, data.paymentMethod);
        }

        if (!parameterService.getSync(enParameters.pu)) {
          parameterService.setParameter(enParameters.pu, `${data.config.maxInstallments}`);
        }

        data.originalContentId = contentId;

        let installments = data.installments;

        if (data.upsell) {
          const oneX = installments.find(x => x.installment === 1);
          installments = [oneX];
        }

        (data.installments as any) = installments;

        socketService.emit('cart', {
          contentId: data.contentId,
          producerId: data.config.producerId
        });

        warmapCache(data);

        if (!IGNORE_CACHE && !data.isCached && !data.user && !data.upsell) {
          cacheService.setCache(CACHE_ID, data);
        }

        if (!data.isCached) {
          this.mounted = true;
        }

        const lang = data.currency === enCurrency.BRL ? enLang.PT_BR : enLang.EN_US;

        pixelService.setPixel({ ...data.config.pixel, contentId });
        currencyService.setCurrency(data.currency);
        translateService.setLang(lang);

        if (!paymentData.contentId) {
          this.initializePayment(data.contentId, transactionKey, data, paymentData);
        }

        paymentService.loadParameters();

        if (data.upsell) {
          paymentService.setPaymentData(0, {
            paymentMethod: data.upsell,
            numberInstallments: 1
          });
          this.initializePayment((isEcommerce ? data.contentId : data.originalContentId), transactionKey, data, paymentData);
        }

        // fix até atualizar nova API
        data.config.enableCreditcard = data.config.enableCreditcard === undefined ? true : data.config.enableCreditcard;

        if (!data.config.enableCreditcard) {
          let pm = null;

          if (data.config.enablePaypal) {
            pm = enPaymentMethod.PAYPAL;
          }

          if (data.config.enablePaypal && lang == enLang.EN_US) {
            pm = enPaymentMethod.PAYPAL;
          }

          if (data.config.enableBankslip && lang != enLang.EN_US) {
            pm = enPaymentMethod.BOLETO;
          }

          if (pm) {
            paymentService.setPaymentData(0, {
              paymentMethod: pm
            });
          }

          if (!pm) {
            data.config.enableCreditcard = true;
          }
        }

        if (data.tickets) {
          ticketService.setTimeLeft(new Date().getTime() + 1000 * data.tickets.reservationData.expiresIn);
        }

        this.cart$.next(data);

        paymentService.setPaymentData(0, { installments });

        if (data.installmentSelected > 0) {
          paymentService.setDefaultInstallment(data.installmentSelected);
        }

        return true;
      })
      .logError();

    return translateService.loadTranslations()
      .switchMap(() => observable);

  }

  private createNewCart(cart: IProductData, coupon: ICouponData) {
    let newCart: IProductData = JSON.parse(JSON.stringify(cart));

    const cartCoupon = coupon.coupon ? {
      code: coupon.coupon.code,
      value: coupon.coupon.value
    } : null;

    newCart = {
      ...newCart,
      ...coupon.product,
      coupon: cartCoupon,
      numberInstallments: coupon.product.installments.length,
      maxInstallments: cart.maxInstallments || coupon.product.installments.length
    };

    return newCart;
  }

  private initializePayment(contentId: string, ssid: string, data: IProductData, paymentData: ICartModel) {
    paymentService.setData({
      contentId,
      ssid,
      fullPrice: Number(data.price.toFixed(2)),
      quantity: data.quantity,
      productType: data.config.productType,
    });

    paymentService.setPaymentData(0, {
      paymentMethod: data.paymentType == enPaymentType.FREE
        ? enPaymentMethod.FREE
        : data.config.isOneClick
          ? enPaymentMethod.ONE_CLICK_BUY
          : null,
      installments: data.installments,
      numberInstallments: data.numberInstallments,
      value: data.price
    });

    if (data.user) {
      const user = data.user || {};
      paymentService.setData({
        name: paymentData.name || user.name,
        email: paymentData.email || user.email,
        phone: paymentData.phone || user.phone,
        document: paymentData.document || user.document
      });

      const currentAddress = paymentData.address || {};

      if (user.postalCode) {
        paymentService.setAddressData({
          postalCode: currentAddress.postalCode || user.postalCode,
          street: currentAddress.street || user.street,
          number: currentAddress.number || user.number,
          district: currentAddress.district || user.district,
          complement: currentAddress.complement || user.complement,
          province: currentAddress.province || user.province,
          cityId: currentAddress.cityId || user.cityId.toString(),
          city: currentAddress.city || user.city
        });
      }

      if (user.creditCard) {
        paymentService.setPaymentData(0, {
          creditCardId: user.creditCard.id,
          brand: user.creditCard.brand,
        });
      }
    }
  }

}

// tslint:disable-next-line:variable-name
export const CartServiceFactory = () => new CartService();

export const cartService = CartServiceFactory();


// WEBPACK FOOTER //
// ./src/services/CartService.ts